/**
 * Interview Service
 * Handles interview-related API calls and business logic
 */

import apiService from '@shared/services/api.service';

// Development mode - set to true to use mock data when backend is unavailable
const USE_MOCK = import.meta.env.DEV && false; // Change to true for mock mode

class InterviewService {
  /**
   * Fetch all interviews for the authenticated candidate
   * @returns {Promise<Object>} { candidate_id, interviews: [...] }
   */
  async fetchCandidateInterviews() {
    try {
      const response = await apiService.getCandidateInterviews();
      console.log('Fetched candidate interviews:', response);
      return response;
    } catch (error) {
      console.error('Failed to fetch candidate interviews:', error);

      // In development, return mock data if backend fails
      if (USE_MOCK) {
        console.warn('Using mock interview data');
        return {
          candidate_id: 5,
          interviews: [
            {
              interview_id: 1,
              room_name: 'interview_candidate_5',
              interview_url: 'wss://livekit.iqarx.com',
              job_title: 'Backend Engineer',
              job_id: 1,
              interview_status: 'scheduled',
              application_status: 'pending',
              created_at: new Date().toISOString(),
            },
          ],
        };
      }

      throw new Error(
        error.message || 'فشل في تحميل المقابلات'
      );
    }
  }

  /**
   * Get interview metadata and LiveKit connection info
   * @param {number} candidateId - Candidate ID
   * @param {number|null} interviewId - Optional Interview ID to load specific interview context
   * @param {string|null} roomName - Optional room name for reconnection (get token for existing room)
   * @returns {Promise<Object>} { room, url, token, candidate_name, job_title }
   */
  async getInterviewMetadata(candidateId, interviewId = null, roomName = null) {
    try {
      const metadata = await apiService.getInterviewMetadata(candidateId, interviewId, roomName);
      console.log('Fetched interview metadata:', metadata);
      return metadata;
    } catch (error) {
      console.error('Failed to fetch interview metadata:', error);

      // In development, return mock data if backend fails
      if (USE_MOCK) {
        console.warn('Using mock interview metadata');
        return {
          room: roomName || `interview_candidate_${candidateId}`,
          url: 'wss://livekit.iqarx.com',
          token: 'mock_livekit_token_' + Date.now(),
          candidate_name: 'Test Candidate',
          job_title: 'Backend Engineer',
        };
      }

      throw new Error(
        error.message || 'فشل في تحميل بيانات المقابلة'
      );
    }
  }

  /**
   * Start an interview session
   * @param {number} candidateId - Candidate ID
   * @param {string} roomName - Room name from LiveKit connection (ensures agent joins same room)
   * @returns {Promise<Object>} { status, room }
   */
  async startInterview(candidateId, roomName = null) {
    try {
      console.log('Starting interview with room:', roomName);
      const response = await apiService.startInterview(candidateId, roomName);
      console.log('Interview started:', response);
      return response;
    } catch (error) {
      console.error('Failed to start interview:', error);

      // In development, return mock data if backend fails
      if (USE_MOCK) {
        console.warn('Using mock start interview response');
        return {
          status: 'started',
          room: roomName || `interview_candidate_${candidateId}`,
        };
      }

      throw new Error(
        error.message || 'فشل في بدء المقابلة'
      );
    }
  }

  /**
   * Generate assessment for interview
   * @param {number} candidateId - Candidate ID
   * @param {string} roomName - LiveKit room name
   * @returns {Promise<Object>} { status, message }
   */
  async generateAssessment(candidateId, roomName) {
    try {
      const response = await apiService.generateAssessment(candidateId, roomName);
      console.log('Assessment generation triggered:', response);
      return response;
    } catch (error) {
      console.error('Failed to trigger assessment generation:', error);
      // Non-blocking - don't throw, just log
      return null;
    }
  }

  /**
   * End an interview session
   * @param {number} candidateId - Candidate ID
   * @param {string|null} roomName - LiveKit room name (for proper session cleanup)
   * @returns {Promise<Object>} { status, room }
   */
  async endInterview(candidateId, roomName = null) {
    try {
      const response = await apiService.endInterview(candidateId, roomName);
      console.log('Interview ended:', response);
      return response;
    } catch (error) {
      console.error('Failed to end interview:', error);

      // In development, return mock data if backend fails
      if (USE_MOCK) {
        console.warn('Using mock end interview response');
        return {
          status: 'completed',
          room: roomName || `interview_candidate_${candidateId}`,
        };
      }

      throw new Error(
        error.message || 'فشل في إنهاء المقابلة'
      );
    }
  }

  /**
   * Check if an interview can be joined
   * @param {Object} interview - Interview object
   * @returns {boolean}
   */
  canJoinInterview(interview) {
    const joinableStatuses = [
      'scheduled',
      'in_progress',
      'interview_scheduled',
    ];

    return (
      joinableStatuses.includes(interview.interview_status) ||
      joinableStatuses.includes(interview.application_status)
    );
  }

  /**
   * Check if an interview is completed
   * @param {Object} interview - Interview object
   * @returns {boolean}
   */
  isInterviewCompleted(interview) {
    return (
      interview.interview_status === 'completed' ||
      interview.application_status === 'completed'
    );
  }

  /**
   * Get interview display status
   * @param {Object} interview - Interview object
   * @returns {string}
   */
  getInterviewDisplayStatus(interview) {
    return interview.interview_status || interview.application_status || 'unknown';
  }
}

// Create singleton instance
const interviewService = new InterviewService();

export default interviewService;
